import { useTheme } from '@/hooks/useTheme';

export default function BrandStatement() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section className="py-32 relative overflow-hidden transition-colors duration-500" style={{ background: isDark ? '#0E1116' : '#F5F5F7' }}>
      {/* Very subtle pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'} 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-16">
          {/* Left — Large quote */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-8">
              <div className={`w-8 h-px ${isDark ? 'bg-white/30' : 'bg-black/20'}`}></div>
              <span className={`text-xs tracking-widest uppercase font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Our Philosophy</span>
            </div>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-8 max-w-xl ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
              Built on Structure.{' '}
              <span className="text-gradient-cyan">Driven by</span>{' '}
              Intelligence.
            </h2>
            <p className={`text-lg leading-relaxed max-w-lg mb-6 ${isDark ? 'text-gray-400' : 'text-[#6e6e73]'}`}>
              PuleAI represents a new class of operational intelligence—where systems are not just deployed, but intentionally designed.
            </p>
            <p className={`text-base leading-relaxed max-w-lg ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Inspired by the concept of "pule" as a form of structured invocation and alignment, our approach centers around creating systems that operate with clarity, consistency, and control.
            </p>

            {/* Divider quote */}
            <div className={`mt-10 pt-8 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`}>
              <p className={`text-xl font-semibold leading-snug italic ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
                "We don't build tools.<br />We build systems that execute."
              </p>
            </div>
          </div>

          {/* Right — Visual grid */}
          <div className="flex-1 grid grid-cols-2 gap-4">
            {/* Big visual */}
            <div className="col-span-2 rounded-2xl overflow-hidden" style={{ height: '220px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/82f7c2d39c2b877b0ee7544b13c3376e.jpg"
                alt="System Architecture"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Stat card 1 */}
            <div className="rounded-2xl p-6 flex flex-col justify-between transition-colors duration-500" style={{ background: isDark ? '#1d1d1f' : '#ffffff', minHeight: '160px' }}>
              <p className={`text-xs tracking-widest uppercase font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Industries Served</p>
              <div>
                <p className={`text-5xl font-bold leading-none mb-1 ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>6+</p>
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>operational sectors</p>
              </div>
            </div>

            {/* Stat card 2 */}
            <div
              className="rounded-2xl p-6 flex flex-col justify-between transition-colors duration-500"
              style={{ background: isDark ? 'rgba(34,211,238,0.08)' : '#E8F4FD', minHeight: '160px', border: isDark ? '1px solid rgba(34,211,238,0.15)' : '1px solid rgba(0,113,227,0.12)' }}
            >
              <p className={`text-xs tracking-widest uppercase font-medium ${isDark ? 'text-cyan-400/70' : 'text-blue-600/70'}`}>Systems Deployed</p>
              <div>
                <p className={`text-5xl font-bold leading-none mb-1 ${isDark ? 'text-cyan-400' : 'text-blue-600'}`}>100+</p>
                <p className={`text-sm ${isDark ? 'text-cyan-400/60' : 'text-blue-600/60'}`}>automations running</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}